/** PRATUS AI drafted the import, or a user ran the Assets tab import dialog. */
export type AssetImportOrigin = 'ai' | 'manual';

/** Where an asset came from, when an import created or updated it. */
export interface AssetImportProvenance {
  resource_set_id: string;
  batch_id: string;
  batch_status: 'committed' | 'undone';
  origin: AssetImportOrigin;
  source_kind: 'file' | 'text';
  source_file_name: string | null;
  user_guid: string;
  committed_at: string | null;
  action_taken: 'created' | 'updated';
}

export interface AssetImportBatchSummary {
  id: string;
  status: 'drafted' | 'committed' | 'undone';
  origin: AssetImportOrigin;
  source_kind: 'file' | 'text';
  source_file_name: string | null;
  committed_at: string | null;
  undone_at: string | null;
  created: number;
  updated: number;
  skipped: number;
}

export function importSourceLabel(value: {
  source_kind: 'file' | 'text';
  source_file_name: string | null;
}): string {
  if (value.source_kind === 'text') return 'a typed list';
  return value.source_file_name || 'a spreadsheet';
}

function formatImportDate(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * "Imported by PRATUS AI from Sites.xlsx, Oct 30, 2026" for AI imports,
 * "Imported from Sites.xlsx, …" for the import dialog (or "Updated …").
 */
export function formatProvenanceLabel(p: AssetImportProvenance): string {
  const verb = p.action_taken === 'updated' ? 'Updated' : 'Imported';
  const by = p.origin === 'manual' ? '' : ' by PRATUS AI';
  const date = formatImportDate(p.committed_at);
  const base = `${verb}${by} from ${importSourceLabel(p)}${date ? `, ${date}` : ''}`;
  return p.batch_status === 'undone' ? `${base} (import undone)` : base;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Banner text for an import the chat just saved or undid. */
export function importBannerMessage(batch: AssetImportBatchSummary): string {
  const source = importSourceLabel(batch);
  if (batch.status === 'undone') {
    return `Import from ${source} was undone; its assets are archived.`;
  }
  const parts = [`${plural(batch.created, 'asset')} imported`];
  if (batch.updated > 0) parts.push(`${batch.updated} updated`);
  return `${parts.join(', ')} from ${source}.`;
}
